import { formatMinorUnits } from '@/api/money';
import type { WorkItem } from '@/api/work';
import { formatInZone, todayInZone } from '@/ui/zonedTime';

/** What a sentence needs to know about the moment and the workspace it is read in. */
export interface RowContext {
  /** The current instant, in epoch milliseconds. */
  readonly now: number;
  /** The workspace's IANA time zone. */
  readonly zone: string;
  /** The workspace's currency, for an amount the server sends without one. */
  readonly currency: string;
}

/** Long enough to say what a note is about; short enough that a row stays a line or two. */
const MAX_QUOTED_CHARACTERS = 80;

const DAY_FORMAT = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' });

/**
 * Text a person wrote, set inside a sentence.
 *
 * Flattened to one line and cut short, so one long note cannot turn a row into a paragraph.
 *
 * @param text what was stored, if anything
 * @returns the text in quotation marks, or null when there is nothing to show
 */
export function quoted(text: string | null): string | null {
  if (text === null) {
    return null;
  }
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat === '') {
    return null;
  }
  const shown =
    flat.length > MAX_QUOTED_CHARACTERS
      ? `${flat.slice(0, MAX_QUOTED_CHARACTERS - 1).trimEnd()}…`
      : flat;
  return `"${shown}"`;
}

/**
 * Adds what a person wrote to a clause.
 *
 * @param base the clause
 * @param lead how the note is introduced, such as "asking about"
 * @param text what was stored, if anything
 * @returns the clause, followed by the note when there is one
 */
export function withNote(base: string, lead: string, text: string | null): string {
  const note = quoted(text);
  return note === null ? base : `${base}, ${lead} ${note}`;
}

/**
 * Joins the parts that are there with single spaces.
 *
 * @param parts pieces of a clause, any of which may be absent
 * @returns the clause
 */
export function words(...parts: readonly (string | null)[]): string {
  return parts.filter((part) => part !== null && part !== '').join(' ');
}

/**
 * A calendar date the way the rest of the product writes one.
 *
 * @param day a date in the workspace calendar, YYYY-MM-DD
 * @returns for example "19 Oct 2026", or null when the date cannot be read
 */
export function dayLabel(day: string): string | null {
  const at = Date.parse(`${day}T00:00:00Z`);
  return Number.isNaN(at) ? null : DAY_FORMAT.format(at);
}

/**
 * An instant as the workspace's clock reads it.
 *
 * @param instant an ISO-8601 instant, if the item has one
 * @param zone the workspace's IANA time zone
 * @returns for example "19 Oct 2026, 17:00 GMT-5", or null when there is no usable instant
 */
export function instantLabel(instant: string | null, zone: string): string | null {
  if (instant === null || Number.isNaN(Date.parse(instant))) {
    return null;
  }
  return formatInZone(instant, zone);
}

/**
 * Where an item's due stands, as a clause that follows its subject.
 *
 * "Was" and "is" follow the clock rather than the section, so a row that has slipped while the page
 * was open reads as late. A date with no time of day is compared in the workspace's calendar.
 *
 * @param item the item
 * @param context the moment and the workspace
 * @returns for example "was due 19 Oct 2026, 17:00 GMT-5", or null when the due is unusable
 */
export function dueClause(item: WorkItem, context: RowContext): string | null {
  if (item.dueAt !== null) {
    const at = Date.parse(item.dueAt);
    if (Number.isNaN(at)) {
      return null;
    }
    return `${at < context.now ? 'was' : 'is'} due ${formatInZone(item.dueAt, context.zone)}`;
  }
  if (item.dueOn !== null) {
    const day = dayLabel(item.dueOn);
    if (day === null) {
      return null;
    }
    const today = todayInZone(context.zone, context.now);
    if (item.dueOn === today) {
      return 'is due today';
    }
    return `${item.dueOn < today ? 'was' : 'is'} due ${day}`;
  }
  return null;
}

/**
 * The money on an item, for a sentence.
 *
 * @param item the item
 * @param context the moment and the workspace
 * @returns the amount in its currency, or null when the item carries none
 */
export function moneyOf(item: WorkItem, context: RowContext): string | null {
  return item.amountMinor === null
    ? null
    : formatMinorUnits(item.amountMinor, item.currency ?? context.currency);
}

/**
 * "for $450.00", for a clause that may or may not have an amount.
 *
 * @param item the item
 * @param context the moment and the workspace
 * @returns the phrase, or null when the item carries no money
 */
export function forAmount(item: WorkItem, context: RowContext): string | null {
  const amount = moneyOf(item, context);
  return amount === null ? null : `for ${amount}`;
}
